import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

interface Race {
  raceName: string;
  circuit: string;
  location: string;
  country: string;
  date: string;
  time: string;
  lat?: number;
  long?: number;
}

interface Weather {
  temperatureF: number;
  code: number;
}

// Minimal subset of WMO weather codes (the set Open-Meteo actually
// returns) mapped to a short label — full table has ~30 entries, this
// covers what a race circuit's forecast realistically shows.
const weatherCodeLabel = (code: number): string => {
  if (code === 0) return 'clear';
  if (code <= 2) return 'partly cloudy';
  if (code === 3) return 'overcast';
  if (code === 45 || code === 48) return 'foggy';
  if (code >= 51 && code <= 57) return 'drizzle';
  if (code >= 61 && code <= 67) return 'rain';
  if (code >= 71 && code <= 77) return 'snow';
  if (code >= 80 && code <= 82) return 'showers';
  if (code >= 95) return 'storms';
  return 'unknown';
};

interface DriverStanding {
  position: number;
  driver: string;
  team: string;
  points: string;
  driverCode: string;
  driverId: string;
  nationality?: string;
  dateOfBirth?: string;
  permanentNumber?: string;
  url?: string;
  wikipediaImage?: string;
  wikipediaDescription?: string;
  age?: number;
  podiums?: number;
  polePositions?: number;
}

interface ConstructorStanding {
  position: number;
  name: string;
  points: string;
  nationality?: string;
  url?: string;
  constructorId?: string;
  wikipediaImage?: string;
  wikipediaDescription?: string;
  base?: string;
  championships?: number;
}

// Helper function to fetch Wikipedia data
const fetchWikipediaData = async (pageTitle: string) => {
  try {
    // Fetch page info including image and extract
    const response = await fetch(
      `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(pageTitle)}`
    );

    if (!response.ok) return null;

    const data = await response.json();

    return {
      image: data.thumbnail?.source || data.originalimage?.source,
      description: data.extract,
    };
  } catch (error) {
    console.error(`Error fetching Wikipedia data for ${pageTitle}:`, error);
    return null;
  }
};

// Helper function to fetch driver career stats from Ergast API
const fetchDriverStats = async (driverId: string) => {
  try {
    const [podiumsRes, polesRes] = await Promise.all([
      fetch(`https://api.jolpi.ca/ergast/f1/drivers/${driverId}/podiums.json?limit=1000`),
      fetch(`https://api.jolpi.ca/ergast/f1/drivers/${driverId}/qualifying/1.json?limit=1000`)
    ]);

    const podiumsData = await podiumsRes.json();
    const polesData = await polesRes.json();

    const podiums = podiumsData.MRData.RaceTable.Races?.length || 0;
    const polePositions = polesData.MRData.RaceTable.Races?.length || 0;

    return { podiums, polePositions };
  } catch (error) {
    console.error(`Error fetching stats for driver ${driverId}:`, error);
    return { podiums: 0, polePositions: 0 };
  }
};


const F1Tile: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [nextRace, setNextRace] = useState<Race | null>(null);
  const [driverStandings, setDriverStandings] = useState<DriverStanding[]>([]);
  const [constructorStandings, setConstructorStandings] = useState<ConstructorStanding[]>([]);
  const [hoveredDriver, setHoveredDriver] = useState<DriverStanding | null>(null);
  const [hoveredTeam, setHoveredTeam] = useState<ConstructorStanding | null>(null);
  const [popupPosition, setPopupPosition] = useState<{ top: number; left: number } | null>(null);
  const closeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const [latestVideoId, setLatestVideoId] = useState<string>('CdKwc1bC44c'); // Default fallback video
  const [isSeasonOver, setIsSeasonOver] = useState<boolean>(false);
  const [weather, setWeather] = useState<Weather | null>(null);

  // Circuit weather — Open-Meteo needs no API key and is CORS-open, so
  // this is a plain client fetch once the next race's coordinates land.
  useEffect(() => {
    if (nextRace?.lat === undefined || nextRace?.long === undefined) return;
    let cancelled = false;

    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${nextRace.lat}&longitude=${nextRace.long}&current=temperature_2m,weather_code&temperature_unit=fahrenheit`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || !data?.current) return;
        setWeather({
          temperatureF: Math.round(data.current.temperature_2m),
          code: data.current.weather_code,
        });
      })
      .catch((err) => console.error('Error fetching circuit weather:', err));

    return () => { cancelled = true; };
  }, [nextRace?.lat, nextRace?.long]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const now = new Date();
        const todayStr = now.toISOString().split('T')[0]; // YYYY-MM-DD

        console.log('Fetching F1 data from Jolpica API...');

        // Add timeout to fetch calls
        const fetchWithTimeout = async (url: string, timeout = 10000) => {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), timeout);

          try {
            const response = await fetch(url, { signal: controller.signal });
            clearTimeout(timeoutId);
            return response;
          } catch (error) {
            clearTimeout(timeoutId);
            throw error;
          }
        };

        // Fetch race schedule
        const scheduleResponse = await fetchWithTimeout('https://api.jolpi.ca/ergast/f1/current.json');
        if (!scheduleResponse.ok) {
          throw new Error(`Failed to fetch schedule: ${scheduleResponse.status}`);
        }

        const scheduleData = await scheduleResponse.json();
        const races = scheduleData.MRData.RaceTable.Races;
        console.log('Races found:', races.length);

        // Find next race
        const upcomingRaces = races.filter((race: any) => race.date >= todayStr);
        if (upcomingRaces.length > 0) {
          const nextRaceData = upcomingRaces[0];
          setNextRace({
            raceName: nextRaceData.raceName,
            circuit: nextRaceData.Circuit.circuitName,
            location: nextRaceData.Circuit.Location.locality,
            country: nextRaceData.Circuit.Location.country,
            date: nextRaceData.date,
            time: nextRaceData.time || '00:00:00Z',
            lat: parseFloat(nextRaceData.Circuit.Location.lat),
            long: parseFloat(nextRaceData.Circuit.Location.long),
          });
          console.log('Next race:', nextRaceData.raceName);
          setIsSeasonOver(false);
        } else {
          // Season is over - hardcode next season's opening race (Australian GP)
          // This typically happens after Abu Dhabi GP until the next season starts
          console.log('No upcoming races in current season - using hardcoded 2026 Australian GP');
          setNextRace({
            raceName: 'Australian Grand Prix',
            circuit: 'Albert Park Grand Prix Circuit',
            location: 'Melbourne',
            country: 'Australia',
            date: '2026-03-08', // 2026 Australian GP - Race day: March 8, 2026
            time: '05:00:00Z', // Race time: 23:00 local (05:00 UTC March 8)
            lat: -37.8497,
            long: 144.968,
          });
          setIsSeasonOver(true);
        }

        // Fetch driver standings - try current year, fallback to previous year if season hasn't started
        let driverStandingsResponse = await fetchWithTimeout('https://api.jolpi.ca/ergast/f1/current/driverStandings.json');
        if (!driverStandingsResponse.ok) {
          throw new Error(`Failed to fetch driver standings: ${driverStandingsResponse.status}`);
        }

        let driverStandingsData = await driverStandingsResponse.json();
        let driversList = driverStandingsData.MRData.StandingsTable.StandingsLists[0]?.DriverStandings || [];

        // If current season standings are empty (season hasn't started), fetch previous year's final standings
        if (driversList.length === 0) {
          console.log('Current season standings not available, fetching 2025 final standings...');
          driverStandingsResponse = await fetchWithTimeout('https://api.jolpi.ca/ergast/f1/2025/driverStandings.json');
          if (driverStandingsResponse.ok) {
            driverStandingsData = await driverStandingsResponse.json();
            driversList = driverStandingsData.MRData.StandingsTable.StandingsLists[0]?.DriverStandings || [];
            // Mark season as over since we're showing final standings from previous year
            setIsSeasonOver(true);
          }
        }

        console.log('Driver standings found:', driversList.length);

        const top5DriversBase = driversList.slice(0, 5).map((standing: any) => ({
          position: parseInt(standing.position),
          driver: `${standing.Driver.givenName} ${standing.Driver.familyName}`,
          team: standing.Constructors[0]?.name || 'Unknown',
          points: standing.points,
          driverCode: standing.Driver.code,
          driverId: standing.Driver.driverId,
          nationality: standing.Driver.nationality,
          dateOfBirth: standing.Driver.dateOfBirth,
          permanentNumber: standing.Driver.permanentNumber,
          url: standing.Driver.url,
        }));

        // Enrich driver data with Wikipedia and stats (with error handling)
        const enrichedDrivers = await Promise.all(
          top5DriversBase.map(async (driver: typeof top5DriversBase[0]) => {
            try {
              // Extract Wikipedia page title from URL
              const wikiTitle = driver.url?.split('/wiki/')[1];

              // Fetch Wikipedia data and stats in parallel with timeout
              const [wikiData, stats] = await Promise.allSettled([
                wikiTitle ? fetchWikipediaData(wikiTitle) : Promise.resolve(null),
                fetchDriverStats(driver.driverId)
              ]);

              // Calculate age from date of birth
              const age = driver.dateOfBirth
                ? new Date().getFullYear() - new Date(driver.dateOfBirth).getFullYear()
                : undefined;

              return {
                ...driver,
                wikipediaImage: wikiData.status === 'fulfilled' ? wikiData.value?.image : undefined,
                wikipediaDescription: wikiData.status === 'fulfilled' ? wikiData.value?.description : undefined,
                age,
                podiums: stats.status === 'fulfilled' ? stats.value.podiums : 0,
                polePositions: stats.status === 'fulfilled' ? stats.value.polePositions : 0,
              };
            } catch (error) {
              console.error(`Error enriching driver ${driver.driver}:`, error);
              // Return basic driver data if enrichment fails
              return {
                ...driver,
                age: driver.dateOfBirth
                  ? new Date().getFullYear() - new Date(driver.dateOfBirth).getFullYear()
                  : undefined,
                podiums: 0,
                polePositions: 0,
              };
            }
          })
        );

        setDriverStandings(enrichedDrivers);

        // Fetch constructor standings (top 5) - try current year, fallback to previous year if season hasn't started
        let constructorStandingsResponse = await fetchWithTimeout('https://api.jolpi.ca/ergast/f1/current/constructorStandings.json');
        if (!constructorStandingsResponse.ok) {
          throw new Error(`Failed to fetch constructor standings: ${constructorStandingsResponse.status}`);
        }

        let constructorStandingsData = await constructorStandingsResponse.json();
        let constructorsList = constructorStandingsData.MRData.StandingsTable.StandingsLists[0]?.ConstructorStandings || [];

        // If current season standings are empty (season hasn't started), fetch previous year's final standings
        if (constructorsList.length === 0) {
          console.log('Current season constructor standings not available, fetching 2025 final standings...');
          constructorStandingsResponse = await fetchWithTimeout('https://api.jolpi.ca/ergast/f1/2025/constructorStandings.json');
          if (constructorStandingsResponse.ok) {
            constructorStandingsData = await constructorStandingsResponse.json();
            constructorsList = constructorStandingsData.MRData.StandingsTable.StandingsLists[0]?.ConstructorStandings || [];
          }
        }

        console.log('Constructor standings found:', constructorsList.length);

        const top5ConstructorsBase = constructorsList.slice(0, 5).map((standing: any) => ({
          position: parseInt(standing.position),
          name: standing.Constructor.name,
          points: standing.points,
          nationality: standing.Constructor.nationality,
          url: standing.Constructor.url,
          constructorId: standing.Constructor.constructorId,
        }));

        // Enrich constructor data with Wikipedia (with error handling)
        const enrichedConstructors = await Promise.allSettled(
          top5ConstructorsBase.map(async (constructor: typeof top5ConstructorsBase[0]) => {
            try {
              // Extract Wikipedia page title from URL
              const wikiTitle = constructor.url?.split('/wiki/')[1];

              // Fetch Wikipedia data with timeout handling
              const wikiData = wikiTitle ? await fetchWikipediaData(wikiTitle) : null;

              return {
                ...constructor,
                wikipediaImage: wikiData?.image,
                wikipediaDescription: wikiData?.description,
              };
            } catch (error) {
              console.error(`Error enriching constructor ${constructor.name}:`, error);
              // Return basic constructor data if enrichment fails
              return constructor;
            }
          })
        ).then(results =>
          results.map((result, index) =>
            result.status === 'fulfilled' ? result.value : top5ConstructorsBase[index]
          )
        );

        setConstructorStandings(enrichedConstructors);

        console.log('F1 data fetch completed successfully');

      } catch (err: any) {
        console.error("Error fetching F1 data:", err);
        setError(err.message || "Failed to fetch F1 data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch F1 video URL from text file
  useEffect(() => {
    const fetchF1VideoFromFile = async () => {
      try {
        const response = await fetch('/content/f1-video.txt');

        if (!response.ok) {
          console.error('Failed to fetch f1-video.txt, status:', response.status);
          throw new Error('Failed to fetch f1-video.txt');
        }

        const videoUrl = await response.text();
        const trimmedUrl = videoUrl.trim();

        console.log('F1 video URL from file:', trimmedUrl);

        // Extract video ID from YouTube URL
        // Supports formats:
        // - https://www.youtube.com/watch?v=VIDEO_ID
        // - https://youtu.be/VIDEO_ID
        // - Or just VIDEO_ID directly
        let videoId = '';

        if (trimmedUrl.includes('youtube.com/watch?v=')) {
          const urlParams = new URLSearchParams(trimmedUrl.split('?')[1]);
          videoId = urlParams.get('v') || '';
        } else if (trimmedUrl.includes('youtu.be/')) {
          videoId = trimmedUrl.split('youtu.be/')[1].split('?')[0];
        } else {
          // Assume it's just the video ID
          videoId = trimmedUrl;
        }

        if (videoId) {
          console.log('Extracted video ID:', videoId);
          setLatestVideoId(videoId);
        } else {
          console.log('No valid video ID found in file');
        }
      } catch (error) {
        console.error('Error fetching F1 video from file:', error);
        // Keep default fallback video ID
      }
    };

    fetchF1VideoFromFile();
  }, []);

  const handleDriverClick = (driver: DriverStanding, event: React.MouseEvent<HTMLDivElement>) => {
    // If clicking the same driver, close the popup
    if (hoveredDriver?.driverId === driver.driverId) {
      setHoveredDriver(null);
      setHoveredTeam(null);
      setPopupPosition(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const popupWidth = 340;
    const popupMaxHeight = window.innerHeight * 0.8; // 80vh
    const spacing = 16;

    // Default to left side of the card
    let left = rect.left - popupWidth - spacing;
    let top = rect.top;

    // Check if popup would go off the left edge
    if (left < spacing) {
      // Position to the right of the card instead
      left = rect.right + spacing;
    }

    // Make sure popup doesn't go off screen on the right
    if (left + popupWidth > window.innerWidth - spacing) {
      left = Math.max(spacing, window.innerWidth - popupWidth - spacing);
    }

    // Ensure popup doesn't go off the bottom of the screen
    if (top + popupMaxHeight > window.innerHeight - spacing) {
      top = Math.max(spacing, window.innerHeight - popupMaxHeight - spacing);
    }

    // Ensure popup doesn't go off the top
    if (top < spacing) {
      top = spacing;
    }

    setHoveredDriver(driver);
    setHoveredTeam(null);
    setPopupPosition({ top, left });
  };

  const handleTeamClick = (team: ConstructorStanding, event: React.MouseEvent<HTMLDivElement>) => {
    // If clicking the same team, close the popup
    if (hoveredTeam?.constructorId === team.constructorId) {
      setHoveredDriver(null);
      setHoveredTeam(null);
      setPopupPosition(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const popupWidth = 340;
    const popupMaxHeight = window.innerHeight * 0.8; // 80vh
    const spacing = 16;

    // Default to left side of the card
    let left = rect.left - popupWidth - spacing;
    let top = rect.top;

    // Check if popup would go off the left edge
    if (left < spacing) {
      // Position to the right of the card instead
      left = rect.right + spacing;
    }

    // Make sure popup doesn't go off screen on the right
    if (left + popupWidth > window.innerWidth - spacing) {
      left = Math.max(spacing, window.innerWidth - popupWidth - spacing);
    }

    // Ensure popup doesn't go off the bottom of the screen
    if (top + popupMaxHeight > window.innerHeight - spacing) {
      top = Math.max(spacing, window.innerHeight - popupMaxHeight - spacing);
    }

    // Ensure popup doesn't go off the top
    if (top < spacing) {
      top = spacing;
    }

    setHoveredTeam(team);
    setHoveredDriver(null);
    setPopupPosition({ top, left });
  };

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Don't close if clicking on a standing row or popup
      if (!target.closest('.f1-standing-row') && !target.closest('.f1-popup')) {
        setHoveredDriver(null);
        setHoveredTeam(null);
        setPopupPosition(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  if (loading) {
    return <div className="bento-item bento-f1 bento-f1-landscape p-4 text-center">Loading F1 data...</div>;
  }

  if (error) {
    return <div className="bento-item bento-f1 bento-f1-landscape p-4 text-center text-red-500">{error}</div>;
  }

  const handleTileMouseLeave = () => {
    setHoveredDriver(null);
    setHoveredTeam(null);
    setPopupPosition(null);
  };

  return (
    <div
      className="bento-item bento-f1 bento-f1-landscape"
      onMouseLeave={handleTileMouseLeave}
    >
      <div className="widget-content f1-landscape-content">
        <div className="f1-header-section">
          <img src="/content/f1-logo.svg" alt="Formula 1" className="f1-logo" />
          <p className="f1-caption">the bits that i enjoy</p>
        </div>

        <div className="f1-landscape-layout">
          {/* Left (narrower) column: Next Race, then Constructors — short
             team names fit fine at this width. Right (wider) column
             carries Drivers, whose full names need the extra room. */}
          <div className="f1-next-race-section">
            <p className="f1-section-title f1-grid-next-title">Next Race</p>
            {nextRace ? (
              <div className="f1-next-race-card f1-grid-next-card">
                <div className="race-header">
                  <h4 className="f1-race-name">{nextRace.raceName}</h4>
                </div>
                <p className="f1-race-location">
                  {nextRace.circuit}, {nextRace.location}
                </p>
                <div className="f1-race-datetime">
                  <div className="datetime-row">
                    <span>{new Date(`${nextRace.date}T${nextRace.time}`).toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                    })}</span>
                  </div>
                  <div className="datetime-row">
                    <span>{new Date(`${nextRace.date}T${nextRace.time}`).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })}</span>
                  </div>
                  {weather && (
                    <div className="datetime-row">
                      <span>{weather.temperatureF}°F, {weatherCodeLabel(weather.code)}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="f1-loading f1-grid-next-card">Loading...</p>
            )}

            <div className="f1-standings-group f1-grid-constructors">
              <p className="f1-standings-label">
                Constructors
              </p>
              <div className="f1-standings-list">
                {constructorStandings.length > 0 ? (
                  constructorStandings.map((constructor, index) => (
                    <div
                      key={constructor.position}
                      className={`f1-standing-row ${index === 0 ? 'leader' : ''} ${hoveredTeam?.constructorId === constructor.constructorId ? 'active' : ''}`}
                      onClick={(e) => handleTeamClick(constructor, e)}
                    >
                      <span className="standing-position">{constructor.position}</span>
                      <div className="standing-info">
                        <span className="standing-driver">
                          {constructor.name}
                          {isSeasonOver && constructor.position === 1 && (
                            <span className="championship-badge">WCC</span>
                          )}
                        </span>
                      </div>
                      <span className="standing-points">{constructor.points}</span>
                    </div>
                  ))
                ) : (
                  <p className="f1-loading">Loading...</p>
                )}
              </div>
            </div>
          </div>

          {/* Right column: Last Race Highlights, then Drivers. */}
          <div className="f1-standings-section">
            <div className="f1-highlights-section">
              <p className="f1-section-title f1-grid-highlights-title">Last Race Highlights</p>
              <a
                href={`https://www.youtube.com/watch?v=${latestVideoId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="f1-highlights-container"
              >
                <img
                  src={`https://img.youtube.com/vi/${latestVideoId}/maxresdefault.jpg`}
                  alt="F1 Race Highlights"
                  className="f1-highlights-thumbnail"
                  onError={(e) => {
                    // Fallback to standard quality if maxres not available
                    e.currentTarget.src = `https://img.youtube.com/vi/${latestVideoId}/hqdefault.jpg`;
                  }}
                />
                <div className="f1-highlights-overlay">
                  <div className="f1-play-button">
                    <svg width="68" height="48" viewBox="0 0 68 48">
                      <path fill="#E50914" d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z"></path>
                      <path fill="#fff" d="M 45,24 27,14 27,34"></path>
                    </svg>
                  </div>
                  <span className="f1-highlights-text">Watch on YouTube</span>
                </div>
              </a>
            </div>

            <div className="f1-standings-group f1-grid-drivers">
              <p className="f1-standings-label">
                Drivers
              </p>
              <div className="f1-standings-list">
                {driverStandings.length > 0 ? (
                  driverStandings.map((driver, index) => (
                    <div
                      key={driver.position}
                      className={`f1-standing-row ${index === 0 ? 'leader' : ''} ${hoveredDriver?.driverId === driver.driverId ? 'active' : ''}`}
                      onClick={(e) => handleDriverClick(driver, e)}
                    >
                      <span className="standing-position">{driver.position}</span>
                      <div className="standing-info">
                        <span className="standing-driver">
                          {driver.driver}
                          {isSeasonOver && driver.position === 1 && (
                            <span className="championship-badge">WDC</span>
                          )}
                        </span>
                        <span className="standing-team">{driver.team}</span>
                      </div>
                      <span className="standing-points">{driver.points}</span>
                    </div>
                  ))
                ) : (
                  <p className="f1-loading">Loading...</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Driver Popup - Rendered via Portal */}
      {hoveredDriver && popupPosition && ReactDOM.createPortal(
        <div
          className="f1-popup f1-driver-popup"
          style={{
            top: `${popupPosition.top}px`,
            left: `${popupPosition.left}px`,
          }}
        >
          {hoveredDriver.wikipediaImage && (
            <div className="f1-popup-image-wrapper">
              <img
                src={hoveredDriver.wikipediaImage}
                alt={hoveredDriver.driver}
                className="f1-popup-image"
                loading="lazy"
              />
            </div>
          )}
          <div className="f1-popup-content">
            <h3 className="f1-popup-title">{hoveredDriver.driver}</h3>
            {hoveredDriver.wikipediaDescription && (
              <p className="f1-popup-description">
                {hoveredDriver.wikipediaDescription.length > 120
                  ? hoveredDriver.wikipediaDescription.substring(0, 120) + '...'
                  : hoveredDriver.wikipediaDescription}
              </p>
            )}
            <div className="f1-popup-tags">
              {hoveredDriver.nationality && (
                <span className="f1-popup-tag">{hoveredDriver.nationality}</span>
              )}
              {hoveredDriver.age && (
                <span className="f1-popup-tag">{hoveredDriver.age} years</span>
              )}
              {hoveredDriver.permanentNumber && (
                <span className="f1-popup-tag">#{hoveredDriver.permanentNumber}</span>
              )}
            </div>
            <div className="f1-popup-stats">
              <div className="f1-popup-stat">
                <span className="f1-popup-stat-label">Team</span>
                <span className="f1-popup-stat-value">{hoveredDriver.team}</span>
              </div>
              <div className="f1-popup-stat">
                <span className="f1-popup-stat-label">Points</span>
                <span className="f1-popup-stat-value">{hoveredDriver.points}</span>
              </div>
              {hoveredDriver.podiums !== undefined && hoveredDriver.podiums > 0 && (
                <div className="f1-popup-stat">
                  <span className="f1-popup-stat-label">Podiums</span>
                  <span className="f1-popup-stat-value">{hoveredDriver.podiums}</span>
                </div>
              )}
              {hoveredDriver.polePositions !== undefined && hoveredDriver.polePositions > 0 && (
                <div className="f1-popup-stat">
                  <span className="f1-popup-stat-label">Poles</span>
                  <span className="f1-popup-stat-value">{hoveredDriver.polePositions}</span>
                </div>
              )}
            </div>
          </div>
          {hoveredDriver.url && (
            <a
              href={hoveredDriver.url}
              target="_blank"
              rel="noopener noreferrer"
              className="f1-popup-link"
            >
              View on Wikipedia →
            </a>
          )}
        </div>,
        document.body
      )}

      {/* Team Popup - Rendered via Portal */}
      {hoveredTeam && popupPosition && ReactDOM.createPortal(
        <div
          className="f1-popup f1-team-popup"
          style={{
            top: `${popupPosition.top}px`,
            left: `${popupPosition.left}px`,
          }}
        >
          {hoveredTeam.wikipediaImage && (
            <div className="f1-popup-image-wrapper">
              <img
                src={hoveredTeam.wikipediaImage}
                alt={hoveredTeam.name}
                className="f1-popup-image"
                loading="lazy"
              />
            </div>
          )}
          <div className="f1-popup-content">
            <h3 className="f1-popup-title">{hoveredTeam.name}</h3>
            {hoveredTeam.wikipediaDescription && (
              <p className="f1-popup-description">
                {hoveredTeam.wikipediaDescription.length > 120
                  ? hoveredTeam.wikipediaDescription.substring(0, 120) + '...'
                  : hoveredTeam.wikipediaDescription}
              </p>
            )}
            <div className="f1-popup-tags">
              {hoveredTeam.nationality && (
                <span className="f1-popup-tag">{hoveredTeam.nationality}</span>
              )}
            </div>
            <div className="f1-popup-stats">
              <div className="f1-popup-stat">
                <span className="f1-popup-stat-label">Points</span>
                <span className="f1-popup-stat-value">{hoveredTeam.points}</span>
              </div>
            </div>
          </div>
          {hoveredTeam.url && (
            <a
              href={hoveredTeam.url}
              target="_blank"
              rel="noopener noreferrer"
              className="f1-popup-link"
            >
              View on Wikipedia →
            </a>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default F1Tile;
